/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { DeviceModel } from '../../core/device';
import { BrandingUiInfoKeys } from '../../core/entities/BrandingUiInfoKeys';
import { ErrorTypes } from '../../core/error';
import { ServicesClientImpl } from '../../core/interactors';
import { Locale, LocaleKeys } from '../../core/locale';
import { Log } from '../../core/logging';
import {
    CursorManager,
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../core/navigation';
import { TScreen } from '../../core/navigation/types';
import { NetworkManager } from '../../core/network/NetworkManager';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { TOfferPreviewCustomData } from '../../core/types';
import { ListTypes, UniqueIdentifier } from '../../core/utils';
import { setListEntryFocus } from '../../store/list/list.actions';
import { resetMyShows } from '../../store/myshows/myshows.actions';
import {
    getMyShowsItems,
    onCursorStateChangeMyShowsOperations,
    setInitialValuesForMyShows,
} from '../../store/myshows/myshows.reducers';
import { MyShowsListGenerator } from '../../store/myshows/MyShowsListGenerator';
import { VisibilityState } from '../../store/visibilityevents/visibilitystate';
import { showWhisper } from '../../store/whisper/whisper.reducer';
import { ClockTypes } from '../Clock';
import { AntiburnOsd, Mousetrap, ScreenFooter, ScreenHeader, ScreenPreview } from '../common';
import List from '../common/List/List';
import { useScreenController } from '../hooks';
import { Whisper } from '../Whisper';
import { InteractiveLayer } from './InteractiveLayer';
import { ListItem } from './ListItem';
import { Preview } from './Preview';

import './MyShowsScreen.scss';

const MyShowsScreen: FunctionComponent = (): JSX.Element => {
    const TAG: string = 'MyShowsScreen';

    useScreenController<TScreen>({
        identifier: TAG,
        properties: {
            url: LocationUrls.MY_SHOWS_SCREEN,
            screenController: ScreenControllerTypes.MY_SHOWS_SCREEN,
            taskController: ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS,
        },
    });

    const dispatch = useTivoDispatch();

    const isScreenMounted = useRef(false);

    const filterList = useTivoSelector((state: RootState) => state.list[ListTypes.MYSHOWS_FILTER]);
    const itemList = useTivoSelector((state: RootState) => state.list[ListTypes.MYSHOWS_ITEMS]);
    const { isItemsLoading, myShowsFilter, myShowsItems } = useTivoSelector(
        (state: RootState) => state.myShows,
    );
    const { isActive: isAntiBurnOsdActive } = useTivoSelector(
        (state: RootState) => state.antiburnOsd,
    );
    const { visibilityState } = useTivoSelector((state: RootState) => state.visibilityEvents);
    const { isOverlayDialogActive } = useTivoSelector((state: RootState) => state.overlayDialog);
    const hasError = useTivoSelector((state: RootState) => state.myShows.hasError);
    const errorCode = useTivoSelector((state: RootState) => state.myShows.errorCode);
    const isVideoWindowActive = DeviceModel.getAppSettings().isVideoWindowEnabled();

    const currentFilter = myShowsFilter?.[filterList?.position || 0] || {};
    const isPausedFilter = currentFilter?.value === 'paused';

    const [previewData, setPreviewData] = useState<TOfferPreviewCustomData | undefined>(undefined);
    const [isCursorActive, setisCursorActive] = useState<boolean>(CursorManager.isCursorAppeared());

    const title = Locale.getString(LocaleKeys.MY_SHOWS_HEADER_TITLE);

    const emptyFilterMessage = {
        error: Locale.getString(LocaleKeys.MY_SHOWS_ERROR_MESSAGE),
        default: Locale.getString(LocaleKeys.MY_SHOWS_DEFAULT),
        recordings: Locale.getString(LocaleKeys.MY_SHOWS_RECORDINGS),
        allShows: Locale.getString(LocaleKeys.MY_SHOWS_ALL_SHOWS),
        tvSeries: Locale.getString(LocaleKeys.MY_SHOWS_TV_SERIES),
        movies: Locale.getString(LocaleKeys.MY_SHOWS_MOVIES),
        kids: Locale.getString(LocaleKeys.MY_SHOWS_KIDS),
        sports: Locale.getString(LocaleKeys.MY_SHOWS_SPORTS),
        paused: Locale.getString(LocaleKeys.MY_SHOWS_PAUSED),
    };

    const services = new ServicesClientImpl();
    const secondaryBranding = services.getAppUiImageByKey(
        BrandingUiInfoKeys.APP_ICONS.SECONDARY_BRANDING,
    );
    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);
    const shouldHideAdultContent = services.shouldHideMetadata();

    const onCursorStateChangeCallback = (state: boolean) => {
        setisCursorActive(state);
        dispatch(onCursorStateChangeMyShowsOperations());
    };

    const onNetworkStateChangeCallback = () => {
        const isDisabled = NetworkManager.isNetworkDisconnected();

        if (isDisabled) {
            // show whisper regarding network disconnection
            const message = `${Locale.getString(LocaleKeys.NO_NETWORK_OVERLAY_DESCRIPTION)} (${ErrorTypes.MY_SHOWS_NETWORK_DISCONNECTED_ERROR_C1505})`;
            dispatch(showWhisper({ message }));
        }
    };

    const init = async () => {
        dispatch(setInitialValuesForMyShows());
    };

    const getPausedPreview = async () => {
        if (isPausedFilter && myShowsItems.myShows?.length && itemList && itemList.isFocused) {
            const { recordingId } = myShowsItems.myShows[itemList.position];

            try {
                const res = await services.fetchRecordingPreview({
                    recordingId: recordingId as string,
                    applicationFeatureArea: 'myShows',
                    networkCallId: UniqueIdentifier.getNextGuidePreviewNetworkCallId(),
                });

                if (res.networkCallId === UniqueIdentifier.getGuidePreviewNetworkCallId())
                    setPreviewData(res.offerPreview);
            } catch (_error) {
                const errorMessage =
                    _error instanceof Error ? _error.message : JSON.stringify(_error);
                Log.e(TAG, errorMessage);
                Diagnostics.error(TAG, errorMessage);
            }
        }
    };

    useEffect(() => {
        if (isScreenMounted.current && !isOverlayDialogActive) {
            dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, true));
        }
    }, [isOverlayDialogActive]);

    useEffect(() => {
        if (
            isScreenMounted.current &&
            visibilityState === VisibilityState.ON_RESUME &&
            !isItemsLoading
        ) {
            /* When the user sends the app to the background in cursor mode and returns to
         the foreground in keyboard mode, handle key navigation appropriately. */
            dispatch(onCursorStateChangeMyShowsOperations());
            if (myShowsItems.myShows.length === 0) dispatch(getMyShowsItems(false));
        }
    }, [visibilityState]);

    useEffect(() => {
        init();
        isScreenMounted.current = true;
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const refNetworkManager = NetworkManager.subscribe(onNetworkStateChangeCallback);

        Diagnostics.info(TAG, 'MyShowsScreen mounted.');
        Log.i(TAG, 'MyShowsScreen mounted.');

        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        return () => {
            isScreenMounted.current = false;
            CursorManager.unsubscribe(refCursorManager);
            NetworkManager.unsubscribe(refNetworkManager);

            MyShowsListGenerator.reset();
            dispatch(resetMyShows());

            Diagnostics.info(TAG, 'MyShowsScreen unmounted.');
            Log.i(TAG, 'MyShowsScreen unmounted.');
        };
    }, []);

    useEffect(() => {
        getPausedPreview();

        return () => {
            setPreviewData(undefined);
        };
    }, [itemList, myShowsItems]);

    return (
        <React.Fragment>
            <div className="myshows-container">
                <ScreenHeader title={title} clockType={ClockTypes.DEFAULT_CLOCK} />

                <div className="myshows-body">
                    <div className="filters">
                        <List
                            type={ListTypes.MYSHOWS_FILTER}
                            itemList={myShowsFilter}
                            initialFocusedIndex={0}
                            initialSelectedIndex={0}
                            generateItemView={(item, itemClass, index) => (
                                <Mousetrap
                                    key={index}
                                    value={`${index}_${ListTypes.MYSHOWS_FILTER}`}
                                    className={`item ${itemClass}`}
                                >
                                    <span className="title">{item.label}</span>
                                </Mousetrap>
                            )}
                        />
                    </div>

                    <div className="items">
                        <List
                            type={ListTypes.MYSHOWS_ITEMS}
                            itemList={myShowsItems.myShows}
                            generateItemView={(item, itemClass, index) => (
                                <Mousetrap
                                    key={index}
                                    value={`${index}_${ListTypes.MYSHOWS_ITEMS}`}
                                    className={`item ${itemClass}`}
                                >
                                    <ListItem
                                        item={item}
                                        sortBy={
                                            myShowsItems.sortOrder?.includes('name')
                                                ? 'name'
                                                : 'date'
                                        }
                                        newIcon={newIcon}
                                        shouldHideAdultContent={shouldHideAdultContent}
                                        isPaused={isPausedFilter}
                                    />
                                </Mousetrap>
                            )}
                        />
                        {!isItemsLoading && myShowsItems.myShows.length <= 0 && (
                            <div
                                className={`empty-list ${isVideoWindowActive ? 'adjust-width' : ''}`}
                            >
                                {!hasError
                                    ? emptyFilterMessage[currentFilter.value]
                                    : emptyFilterMessage.error.replace('$ERROR_CODE', errorCode)}
                            </div>
                        )}
                    </div>
                </div>

                {isPausedFilter && (
                    <ScreenPreview classStyle="myshows-preview">
                        {previewData && <Preview offer={previewData} />}
                    </ScreenPreview>
                )}

                <ScreenFooter image={secondaryBranding}>
                    <ul className="myshows-footer">
                        {currentFilter.value !== 'paused' && (
                            <li className="sort-order">
                                {myShowsItems.sortOrder?.includes('name')
                                    ? `${Locale.getString(LocaleKeys.SORTED_BY_NAME)}`
                                    : `${Locale.getString(LocaleKeys.SORTED_BY_DATE)}`}
                            </li>
                        )}
                        <li className="filter">{currentFilter.label}</li>
                    </ul>
                </ScreenFooter>
            </div>
            {isAntiBurnOsdActive && <AntiburnOsd />}
            {DeviceModel.isWebOsPlatform() && isCursorActive ? <InteractiveLayer /> : null}
            <Whisper message="" />
        </React.Fragment>
    );
};

export default MyShowsScreen;
